import type { z } from 'zod';
import { env } from '../env';
import { ApiError, NETWORK_ERROR_MESSAGE, type ApiErrorDetail } from './errors';

export type RequestOptions<S extends z.ZodType | undefined = undefined> = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Schema para validar a resposta: o app nunca confia cegamente no formato recebido. */
  schema?: S;
  /** Status HTTP de erro que devem ser tratados como resposta válida (ex.: 503 do /ready). */
  acceptStatus?: number[];
  /** Outro servidor que não o da configuração (testes, diagnóstico). */
  baseUrl?: string;
  headers?: Record<string, string>;
};

type ErrorBody = { error?: { code?: string; message?: string; details?: ApiErrorDetail[] } };

export function buildUrl(path: string, query?: RequestOptions['query'], base = env.apiUrl): string {
  // Caminho relativo à base, que pode ter caminho próprio (ex.: https://dominio/api): um caminho absoluto em
  // new URL descartaria o /api da base.
  const url = new URL(path.replace(/^\/+/, ''), `${base.replace(/\/+$/, '')}/`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Retry-After em segundos (o servidor sobrecarregado pede para esperar antes de repetir — T-254). */
function retryAfterOf(res: Response): number | undefined {
  const value = Number(res.headers.get('retry-after'));
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

/** Erro de uma resposta não-ok, com o código e a mensagem do servidor. */
async function errorOf(res: Response): Promise<ApiError> {
  const requestId = res.headers.get('x-request-id') ?? undefined;
  const error = ((await parseBody(res)) as ErrorBody | undefined)?.error;
  return new ApiError(
    res.status,
    error?.code ?? `HTTP_${res.status}`,
    error?.message ?? `O servidor respondeu com erro ${res.status}.`,
    error?.details ?? [],
    requestId,
    retryAfterOf(res),
  );
}

/** Baixa um arquivo da API (ex.: o PDF da ata): o conteúdo e o tipo; erro como em apiRequest. */
export async function apiDownload(
  path: string,
  opts: Pick<RequestOptions, 'query' | 'signal' | 'baseUrl' | 'headers'> = {},
): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query, opts.baseUrl ?? env.apiUrl), {
      method: 'GET',
      headers: { accept: '*/*', ...opts.headers },
      signal: opts.signal,
      credentials: 'include',
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK_ERROR', NETWORK_ERROR_MESSAGE);
  }
  if (!res.ok) throw await errorOf(res);
  return res.blob();
}

/** Arquivo baixado na hora ou a resposta 202 (o relatório grande foi para a fila — T-201c/d). */
export type FileOrQueued = { kind: 'file'; blob: Blob } | { kind: 'queued'; body: unknown };

/** Exportação de relatório: 200 é o arquivo; 202 é o pedido na fila (JSON). */
export async function apiFileOrQueued(
  path: string,
  opts: Pick<RequestOptions, 'query' | 'signal' | 'baseUrl' | 'headers'> = {},
): Promise<FileOrQueued> {
  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query, opts.baseUrl ?? env.apiUrl), {
      method: 'GET',
      headers: { accept: '*/*', ...opts.headers },
      signal: opts.signal,
      credentials: 'include',
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK_ERROR', NETWORK_ERROR_MESSAGE);
  }
  if (!res.ok) throw await errorOf(res);
  if (res.status === 202) return { kind: 'queued', body: (await res.json()) as unknown };
  return { kind: 'file', blob: await res.blob() };
}

/**
 * Chamada à API do Fluxus BSC. Lança ApiError com código e mensagem do servidor quando a
 * resposta é de erro, ou com status 0 quando não há resposta (rede).
 */
export async function apiRequest<S extends z.ZodType | undefined = undefined>(
  path: string,
  opts: RequestOptions<S> = {},
): Promise<S extends z.ZodType ? z.output<S> : unknown> {
  const headers: Record<string, string> = { accept: 'application/json', ...opts.headers };
  // FormData (envio de arquivo): o navegador monta o multipart e o boundary do Content-Type.
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  if (opts.body !== undefined && !isForm) headers['content-type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query, opts.baseUrl ?? env.apiUrl), {
      method: opts.method ?? 'GET',
      headers,
      body: isForm
        ? (opts.body as FormData)
        : opts.body !== undefined
          ? JSON.stringify(opts.body)
          : undefined,
      signal: opts.signal,
      credentials: 'include',
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK_ERROR', NETWORK_ERROR_MESSAGE);
  }

  const requestId = res.headers.get('x-request-id') ?? undefined;
  const body = await parseBody(res);

  if (!res.ok && !opts.acceptStatus?.includes(res.status)) {
    const error = (body as ErrorBody | undefined)?.error;
    throw new ApiError(
      res.status,
      error?.code ?? `HTTP_${res.status}`,
      error?.message ?? `O servidor respondeu com erro ${res.status}.`,
      error?.details ?? [],
      requestId,
      retryAfterOf(res),
    );
  }

  if (opts.schema) {
    const parsed = opts.schema.safeParse(body);
    if (!parsed.success) {
      throw new ApiError(res.status, 'INVALID_RESPONSE', 'Resposta inesperada do servidor.', [], requestId);
    }
    return parsed.data as never;
  }
  return body as never;
}
